import { Injectable, Type } from '@angular/core';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { TranslateService } from '@ngx-translate/core';
import { DataType, EntityType } from '@vendasta/automata';
import { Activity } from '@vendasta/automata/lib/_internal/objects';
import { CRMApiService, CRMCompanyApiService } from '@vendasta/crm';
import { CrmObjectInterface } from '@vendasta/crm/lib/_internal/interfaces/crm-objects.interface';
import { IAMService, User } from '@vendasta/iam';
import { Order } from '@vendasta/sales-orders';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { SalesOrdersService } from '../data-services/sales-orders.service';
import { ColumnDefinition, ColumnType, CustomCellComponent, Pinned } from '@vendasta/va-filter2-table';
import {
  BusinessInfoCellComponent,
  CrmCompanyCellComponent,
  CrmContactCellComponent,
  NoEntityCellComponent,
  OrderBusinessInfoCellComponent,
  OrderCellComponent,
  UserCellComponent,
} from './table-cells';
import { GalaxyColumnDef } from '@vendasta/galaxy/table';

export interface ActivityRow {
  activity: Activity;
  accountGroup?: AccountGroup;
  order?: Order;
  user?: User;
  contact?: CrmObjectInterface;
  company?: CrmObjectInterface;
}

/******************************************************************************/
/* This file consolidates most of the usages of the different entity types    */
/* into one place so that it is easier to know what you need to do to add a   */
/* new entity type. No consideration has been made to make this into a good   */
/* programming pattern (builder/strategy(?)). That is probably the next steps */
/* to refactor out what it means to be an entity type                         */
/*                                                                            */
/* You will also need to look at the automata/pages module entities.ts file   */
/* for displaying columns of entities in the activities and runs tables       */

/******************************************************************************/

export interface EntityComponentRequirements {
  activityTableCellComponent: Type<CustomCellComponent<ActivityRow>> | null;
}

export const entityTypeComponentRequirements: { [entityType: number]: EntityComponentRequirements } = {
  [EntityType.ENTITY_TYPE_NOT_SPECIFIED]: {
    activityTableCellComponent: BusinessInfoCellComponent,
  } as EntityComponentRequirements,
  [EntityType.ENTITY_TYPE_ACCOUNT_GROUP]: {
    activityTableCellComponent: BusinessInfoCellComponent,
  } as EntityComponentRequirements,
  [EntityType.ENTITY_TYPE_ORDER]: {
    activityTableCellComponent: null, // This one is custom, don't have a good interface for that right now
  } as EntityComponentRequirements,
  [EntityType.ENTITY_TYPE_USER]: {
    activityTableCellComponent: UserCellComponent,
  } as EntityComponentRequirements,
  [EntityType.ENTITY_TYPE_NO_ENTITY]: {
    activityTableCellComponent: NoEntityCellComponent,
  } as EntityComponentRequirements,
  [EntityType.ENTITY_TYPE_CONTACT]: {
    activityTableCellComponent: CrmContactCellComponent,
  } as EntityComponentRequirements,
  [EntityType.ENTITY_TYPE_COMPANY]: {
    activityTableCellComponent: CrmCompanyCellComponent,
  } as EntityComponentRequirements,
  // If you are adding a new entity type, don't forget to add it to EntityObservables and fill it in for getEntityObservablesFromActivitiesBasedOnType
};

// This exists while we transition between the va-table to the Galaxy table
export interface DynamicColumn extends GalaxyColumnDef {
  cellComponent: Type<any>;
}

export function buildGalaxyTableEntityDisplayColumns(entityType: EntityType): DynamicColumn[] {
  const entity = entityTypeRequirements[entityType];
  const entityComponents = entityTypeComponentRequirements[entityType];
  switch (entityType) {
    case EntityType.ENTITY_TYPE_ORDER:
      return [
        {
          id: 'order',
          title: 'COMMON.ORDER',
          pinned: true,
          cellComponent: OrderCellComponent,
          sortable: false,
        },
        {
          id: 'order-business',
          title: 'AUTOMATIONS.COMMON.ACCOUNT',
          cellComponent: OrderBusinessInfoCellComponent,
          sortable: false,
        },
      ] as DynamicColumn[];
    case EntityType.ENTITY_TYPE_NO_ENTITY:
      return [] as DynamicColumn[];
    default:
      return [
        {
          id: getActivityTableId(entityType),
          title: entity.singularName,
          pinned: true,
          cellComponent: entityComponents.activityTableCellComponent,
        },
      ] as DynamicColumn[];
  }
}

export const entityTypeRequirements: { [entityType: number]: EntityTypeRequirements } = {
  [EntityType.ENTITY_TYPE_NOT_SPECIFIED]: {
    // Default to account group to limp by but this shouldn't happen
    singularName: 'COMMON.ACCOUNT',
    pluralName: 'COMMON.ACCOUNTS',
    singularNameWithArticle: 'COMMON.AN_ACCOUNT',
    currentlyWaitingTableCellDisplayField: '', // This one is custom, don't have a good interface for that now
    webhookDisplayReplacementString: '{{ accountId }}',
    correspondingDataType: DataType.DATA_TYPE_ACCOUNT_GROUP_ID,
    userFriendlyIDLabel: 'AUTOMATIONS.COMMON.ACCOUNT_ID',
  } as EntityTypeRequirements,
  [EntityType.ENTITY_TYPE_ACCOUNT_GROUP]: {
    singularName: 'COMMON.ACCOUNT',
    pluralName: 'COMMON.ACCOUNTS',
    singularNameWithArticle: 'COMMON.AN_ACCOUNT',
    currentlyWaitingTableCellDisplayField: '', // This one is custom, don't have a good interface for that right now
    webhookDisplayReplacementString: '{{ accountId }}',
    correspondingDataType: DataType.DATA_TYPE_ACCOUNT_GROUP_ID,
    userFriendlyIDLabel: 'AUTOMATIONS.COMMON.ACCOUNT_ID',
  } as EntityTypeRequirements,
  [EntityType.ENTITY_TYPE_ORDER]: {
    singularName: 'COMMON.ORDER',
    pluralName: 'COMMON.ORDERS',
    singularNameWithArticle: 'COMMON.AN_ORDER',
    currentlyWaitingTableCellDisplayField: 'order.orderId',
    webhookDisplayReplacementString: '{{ orderId }}',
    correspondingDataType: DataType.DATA_TYPE_ORDER_ID,
    userFriendlyIDLabel: 'AUTOMATIONS.COMMON.ORDER_ID',
  } as EntityTypeRequirements,
  [EntityType.ENTITY_TYPE_USER]: {
    singularName: 'COMMON.USER',
    pluralName: 'COMMON.USERS',
    singularNameWithArticle: 'COMMON.A_USER',
    currentlyWaitingTableCellDisplayField: 'user.email',
    webhookDisplayReplacementString: '{{ userId }}',
    correspondingDataType: DataType.DATA_TYPE_SMB_USER_ID,
    userFriendlyIDLabel: 'AUTOMATIONS.COMMON.USER_ID',
  } as EntityTypeRequirements,
  [EntityType.ENTITY_TYPE_NO_ENTITY]: {
    singularName: 'AUTOMATIONS.COMMON.ENTITY',
    pluralName: 'AUTOMATIONS.COMMON.AN_ENTITY',
    singularNameWithArticle: 'AUTOMATIONS.COMMON.ENTITIES',
    // NO entity type is not finished yet
    currentlyWaitingTableCellDisplayField: '',
    webhookDisplayReplacementString: '',
    correspondingDataType: DataType.DATA_TYPE_UNSPECIFIED,
    userFriendlyIDLabel: '',
  } as EntityTypeRequirements,
  [EntityType.ENTITY_TYPE_CONTACT]: {
    singularName: 'AUTOMATIONS.COMMON.CONTACT',
    pluralName: 'AUTOMATIONS.COMMON.CONTACTS',
    singularNameWithArticle: 'AUTOMATIONS.COMMON.A_CONTACT',
    // NO entity type is not finished yet
    currentlyWaitingTableCellDisplayField: 'contact.crmObjectId',
    webhookDisplayReplacementString: '{{ contactId }}',
    correspondingDataType: DataType.DATA_TYPE_CONTACT_ID,
    userFriendlyIDLabel: 'AUTOMATIONS.COMMON.CONTACT_ID',
  } as EntityTypeRequirements,
  [EntityType.ENTITY_TYPE_COMPANY]: {
    singularName: 'AUTOMATIONS.COMMON.COMPANY',
    pluralName: 'AUTOMATIONS.COMMON.COMPANIES',
    singularNameWithArticle: 'AUTOMATIONS.COMMON.A_COMPANY',
    // NO entity type is not finished yet
    currentlyWaitingTableCellDisplayField: 'company.crmObjectId',
    webhookDisplayReplacementString: '{{ companyId }}',
    correspondingDataType: DataType.DATA_TYPE_COMPANY_ID,
    userFriendlyIDLabel: 'AUTOMATIONS.COMMON.COMPANY_ID',
  } as EntityTypeRequirements,
  // If you are adding a new entity type, don't forget to add it to EntityObservables and fill it in for getEntityObservablesFromActivitiesBasedOnType
};

@Injectable({ providedIn: 'root' })
export class EntityTypeService {
  constructor(
    private accountGroupService: AccountGroupService,
    private salesOrderService: SalesOrdersService,
    private readonly iamService: IAMService,
    private translateService: TranslateService,
    private readonly contactService: CRMApiService,
    private readonly companiesService: CRMCompanyApiService,
  ) {}

  public getEntityObservablesFromActivitiesBasedOnType(
    partnerId$: Observable<string>,
    activities$: Observable<Activity[]>,
    entityType: EntityType,
  ): EntityObservables {
    const accountGroups$ = this.getMultiEntitiesFromActivities(
      partnerId$,
      activities$,
      entityType,
      EntityType.ENTITY_TYPE_ACCOUNT_GROUP,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (ids: string[], _: string) => this.accountGroupService.getMulti(ids, new ProjectionFilter({ napData: true })),
    );
    const orders$ = this.getMultiEntitiesFromActivities(
      partnerId$,
      activities$,
      entityType,
      EntityType.ENTITY_TYPE_ORDER,
      (ids: string[], partnerId: string) => this.salesOrderService.getMultiOrders(partnerId, ids),
    );
    const users$ = this.getMultiEntitiesFromActivities(
      partnerId$,
      activities$,
      entityType,
      EntityType.ENTITY_TYPE_USER,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (ids: string[], _: string) => this.iamService.getMultiUsers(ids.map((id) => ({ userId: id }))),
    );
    const contacts$ = this.getMultiEntitiesFromActivities(
      partnerId$,
      activities$,
      entityType,
      EntityType.ENTITY_TYPE_CONTACT,
      (ids: string[], namespace: string) =>
        this.contactService
          .getMultiContact({ namespace: namespace, crmObjectIds: ids })
          .pipe(map((res) => res.crmObjects)),
    );
    const companies$ = this.getMultiEntitiesFromActivities(
      partnerId$,
      activities$,
      entityType,
      EntityType.ENTITY_TYPE_COMPANY,
      (ids: string[], namespace: string) =>
        this.companiesService
          .getMultiCompany({ namespace: namespace, crmObjectIds: ids })
          .pipe(map((res) => res.crmObjects)),
    );

    return {
      accountGroups$: accountGroups$,
      orders$: orders$,
      users$: users$,
      contacts$: contacts$,
      companies$: companies$,
    };
  }

  private getMultiEntitiesFromActivities(
    partnerId$: Observable<string>,
    activities$: Observable<Activity[]>,
    entityType: EntityType,
    requiredEntityType: EntityType,
    f: (ids: string[], partnerId: string) => Observable<any>,
  ): Observable<any> {
    return combineLatest([partnerId$, activities$]).pipe(
      switchMap(([partnerId, activities]) => {
        if (activities.length === 0 || entityType !== requiredEntityType) {
          return of([]);
        }
        const ids = activities.map((a) => a.entityId);
        return f(ids, partnerId);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}

export function getActivityTableId(entityType: EntityType): string {
  return 'activity-table-' + entityType;
}

export interface EntityTypeRequirements {
  singularName: string;
  pluralName: string;
  singularNameWithArticle: string;
  currentlyWaitingTableCellDisplayField: string;
  webhookDisplayReplacementString: string;
  correspondingDataType: DataType;
  userFriendlyIDLabel: string;
}

export const validDataTypesForEntities: DataType[] = Object.values(entityTypeRequirements).map(
  (entityType) => entityType.correspondingDataType,
);

export function getEntityIDReplacementString(entityType: EntityType): string {
  const entity = entityTypeRequirements[entityType];
  return entity.webhookDisplayReplacementString;
}

export function convertEntityTypeToDataType(entityType: EntityType): DataType {
  const entity = entityTypeRequirements[entityType];
  return entity.correspondingDataType;
}

export function getSingularEntityTypeDisplayTranslationString(entityType: EntityType): string {
  const entity = entityTypeRequirements[entityType];
  return entity.singularName;
}

export function getSingularEntityTypeWithArticleDisplayTranslationString(entityType: EntityType): string {
  const entity = entityTypeRequirements[entityType];
  return entity.singularNameWithArticle;
}

export function getPluralEntityTypeDisplayTranslationString(entityType: EntityType): string {
  const entity = entityTypeRequirements[entityType];
  return entity.pluralName;
}

export interface EntityObservables {
  accountGroups$: Observable<AccountGroup[]>;
  orders$: Observable<Order[]>;
  users$: Observable<User[]>;
  contacts$: Observable<CrmObjectInterface[]>;
  companies$: Observable<CrmObjectInterface[]>;
}

export function buildVATableEntityDisplayColumns(entityType: EntityType): ColumnDefinition[] {
  const entity = entityTypeRequirements[entityType];
  const entityComponents = entityTypeComponentRequirements[entityType];
  switch (entityType) {
    case EntityType.ENTITY_TYPE_ORDER:
      return [
        {
          id: 'order',
          displayName: 'BUSINESS.ORDER',
          pinned: Pinned.PINNED_LEFT,
          type: ColumnType.COLUMN_TYPE_STRING,
          cellComponent: OrderCellComponent,
        },
        {
          id: 'order-business',
          displayName: 'AUTOMATIONS.COMMON.ACCOUNT',
          type: ColumnType.COLUMN_TYPE_STRING,
          cellComponent: OrderBusinessInfoCellComponent,
        },
      ] as ColumnDefinition[];
    default:
      return [
        {
          id: getActivityTableId(entityType),
          displayName: entity.singularName,
          pinned: Pinned.PINNED_LEFT,
          type: ColumnType.COLUMN_TYPE_STRING,
          cellComponent: entityComponents.activityTableCellComponent,
        },
      ] as ColumnDefinition[];
  }
}
